/**
 * AuthGate — requires a logged-in user before rendering the app.
 *
 * Sits inside <AppAuth0Provider>. When the user isn't authenticated it sends
 * them straight to Auth0's hosted (branded) login — there is no in-app login
 * screen. While Auth0 boots, or the redirect is in flight, it shows a small
 * branded holding screen.
 *
 * This is a UX gate, not the security boundary — the backend's token
 * verification is what actually protects data. Because the provider fails
 * closed, Auth0 is guaranteed configured by the time this renders.
 */

import { useEffect, type ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Loader2 } from 'lucide-react'
import { MatchViewLogo } from '@/components/brand/matchview-logo'
import { PitchBackdrop } from '@/components/brand/pitch-backdrop'

export function AuthGate({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0()

  // Not logged in (and not still booting) → bounce to Auth0's hosted login.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void loginWithRedirect()
    }
  }, [isLoading, isAuthenticated, loginWithRedirect])

  if (isAuthenticated) {
    return <>{children}</>
  }

  // Booting, or about to redirect away — branded holding screen.
  return <AuthHoldingScreen label={isLoading ? 'Loading…' : 'Redirecting to sign in…'} />
}

function AuthHoldingScreen({ label }: { label: string }) {
  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-background px-6">
      <PitchBackdrop />
      <div className="relative z-10 flex flex-col items-center gap-6 animate-fade-in">
        <MatchViewLogo variant="full" size="lg" />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {label}
        </div>
      </div>
    </div>
  )
}
